import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { fetchAreas } from "../redux/slices/areaSlice";
import axios from "axios";
import { server } from "../server";
import { toast } from "react-toastify";
import useAuth from "../useAuth";
import {
  IconButton,
  Pagination,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
} from "@mui/material";
import { FaTrash } from "react-icons/fa";
import { Close } from "@mui/icons-material";


const headersMap = {
  // id: "Scheme ID",
  schemeName: "Scheme Name",
  proposalName: "Proposal Name",
  financialYear: "Financial Year",
  sector: "Sector",
  subSector: "Sub Sector",
  district: "District",
  block: "Block",
  gramPanchayat: "Gram Panchayat",
  landAddress: "Land Address",
  latitude: "Latitude",
  longitude: "Longitude",
  utilityPurpose: "Utility Purpose",
  impactDescription: "Impact Description",
  landNature: "Land Nature",
  landSuitability: "Land Suitability",
  ddcCumCEOName: "DDC cum CEO Name",
  districtEngineerName: "District Engineer Name",
  ddo: "DDO",
  ddoCode: "DDO Code",
  totalAllocated: "Total Allocated Amount",
  allocatedAmount: "Allocated Amount",
  allocationLetterNo: "Allocation Letter No",
  technicalSanctionAmount: "Technical Sanction Amount",
  administrativeApprovalAmount: "Administrative Approval Amount",
  amountRemaining: "Amount Remaining",
  totalSpent: "Amount Spent",
  revenueGenerated: "Revenue Generated Amount",
  ucAmount: "UC Amount",
  needAssessment: "Need Assessment",
  feasibilityStudy: "Feasibility Study",
  dpr: "DPR Status",
  revenueGeneration: "Revenue Generation",
  boardApproval: "Board Approval",
  technicalSanctionAuthority: "Technical Sanction Authority",
  administrativeApprovalAuthority: "Administrative Approval Authority",
  administrativeApprovalLetterNo: "Admin Approval Letter No",
  administrativeApprovalDate: "Admin Approval Date",
  schemeStartDate: "Scheme Start Date",
  completionDate: "Completion Date",
  workOrderDate: "Work Order Date",
  tenderStatus: "Tender Status",
  agencyName: "Agency Name",
  ucSentLetterNo: "UC Sent Letter No",
  ucSentDate: "UC Sent Date",
  lastFieldVisitDate: "Last Field Visit Date",
  fieldVisitOfficials: "Field Visit Officials",
  remarks: "Remarks",
  coReport: "CO Report",
  formStatus: "Form Status",
  schemeStatus: "Scheme Status",
  paymentStatus: "Payment Status",
  progressPercentage: "Progress Percentage"
};

const escapeCSVValue = (val) => {
  if (val === null || val === undefined) return "";
  let stringVal = String(val);
  stringVal = stringVal.replace(/"/g, '""');
  if (stringVal.includes(",") || stringVal.includes('"') || stringVal.includes("\n") || stringVal.includes("\r")) {
    return `"${stringVal}"`;
  }
  return stringVal;
};

const getFieldValue = (scheme, key) => {
  if (key === "latitude") {
    return scheme.coordinates?.latitude || "";
  }
  if (key === "longitude") {
    return scheme.coordinates?.longitude || "";
  }
  if (key === "revenueGeneration" || key === "boardApproval") {
    return scheme[key] ? "Yes" : "No";
  }
  if (key === "revenueGenerated") {
    const rev =
      scheme.revenueGenerated !== undefined && scheme.revenueGenerated !== null
        ? scheme.revenueGenerated
        : scheme.totalRevenueGenerated;
    return rev !== undefined && rev !== null ? rev : 0;
  }
  if (
    key === "administrativeApprovalDate" ||
    key === "schemeStartDate" ||
    key === "completionDate" ||
    key === "workOrderDate" ||
    key === "ucSentDate" ||
    key === "lastFieldVisitDate"
  ) {
    if (!scheme[key]) return "";
    return new Date(scheme[key]).toLocaleDateString("en-IN");
  }
  return scheme[key] !== undefined && scheme[key] !== null ? scheme[key] : "";
};

const getFinancialYearOptions = () => {
  const years = [];
  const currentDate = new Date();
  const currentYear = currentDate.getFullYear();
  const startYear = currentDate.getMonth() >= 3 ? currentYear : currentYear - 1;
  const endYear = 2015;
  for (let y = startYear; y >= endYear; y--) {
    years.push(`${y}-${y + 1}`);
  }
  return years;
};

const SchemesList = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const dispatch = useDispatch();
  const { areas } = useSelector((state) => state.area);

  const [schemes, setSchemes] = useState([]);
  const [schemeStats, setSchemeStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [initialLoading, setInitialLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [filters, setFilters] = useState({
    searchParams: "", // Changed from 'search' to 'searchParams' to match API
    district: "",
    block: "",
    gramPanchayat: "",
    sector: "",
    subSector: "",
    financialYear: "",
    schemeStatus: "",
    tenderStatus: "",
    paymentStatus: "",
    ddcCumCEOName: "",
    districtEngineerName: "",
    ddo: "",
  });

  useEffect(() => {
    dispatch(fetchAreas());
  }, [dispatch]);

  const uniqueDistricts = React.useMemo(() => {
    if (user?.userType !== "admin" && user?.districtName) {
      return [user.districtName];
    }
    const districts = areas?.map((a) => a.district) || [];
    return [...new Set(districts)].sort();
  }, [areas, user]);

  const effectiveDistrict = React.useMemo(() => {
    return (user?.userType !== "admin" ? user?.districtName : filters?.district)?.toUpperCase();
  }, [user, filters?.district]);

  const uniqueBlocks = React.useMemo(() => {
    if (!effectiveDistrict) return [];
    const blocks = (areas || [])
      .filter((a) => a.district === effectiveDistrict)
      .map((a) => a.block);
    return [...new Set(blocks)].sort();
  }, [areas, effectiveDistrict]);

  const financialYearOptions = getFinancialYearOptions();

  const schemeStatusOptions = [
    "Ongoing",
    "Completed",
    "On Hold",
    "Cancelled",
    "Partially Completed",
  ];

  const sectorOptions = [
    "Infrastructure - GP",
    "Asset Creation",
    "Repair/Renovation",
    "Water Supply",
    "Sanitation",
    "Road Construction",
    "Building Construction",
    "Electrical Works",
    "Other",
  ];

  const subSectorOptions = [
    "GP Bhawan - New",
    "GP Bhawan - Repair",
    "Office Renovation",
    "Dak Bungalow",
    "Bus Stand",
    "Market Complex",
    "Marriage Hall",
    "Community Hall",
    "Water Tank",
    "Drainage System",
    "Road Construction",
    "Street Lights",
    "Park Development",
    "Other",
  ];

  const [showFilters, setShowFilters] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const filtersRef = useRef(null);

  // Pagination states
  const [pagination, setPagination] = useState({
    pageNo: 1,
    pageSize: 10,
    totalCount: 0,
    totalPages: 1,
  });

  const [filterOptions, setFilterOptions] = useState({
    districts: [],
    blocks: [],
    gramPanchayats: [],
    sectors: [],
    subSectors: [],
    financialYears: [],
    schemeStatuses: [],
    tenderStatuses: [],
    paymentStatuses: [],
    ddcCumCEOs: [],
    districtEngineers: [],
    ddos: [],
  });

  // Solid color scheme for different filter categories
  const filterCategoryColors = {
    location: {
      bg: "bg-blue-100",
      border: "border-blue-300",
      text: "text-blue-800",
    },
    sector: {
      bg: "bg-purple-100",
      border: "border-purple-300",
      text: "text-purple-800",
    },
    status: {
      bg: "bg-green-100",
      border: "border-green-300",
      text: "text-green-800",
    },
    financial: {
      bg: "bg-orange-100",
      border: "border-orange-300",
      text: "text-orange-800",
    },
    officer: {
      bg: "bg-indigo-100",
      border: "border-indigo-300",
      text: "text-indigo-800",
    },
    search: {
      bg: "bg-gray-100",
      border: "border-gray-300",
      text: "text-gray-800",
    },
  };

  useEffect(() => {
    fetchSchemes();
  }, [filters, pagination.pageNo, pagination.pageSize]);

  // Close filters when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (filtersRef.current && !filtersRef.current.contains(event.target)) {
        setShowFilters(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const fetchSchemes = async () => {
    try {
      setLoading(true);

      // Prepare query parameters
      const params = {
        pageNo: pagination.pageNo,
        pageSize: pagination.pageSize,
        userWise: "true",
        searchParams: filters.searchParams,
        schemeType: "New" // Changed from search to searchParams
      };

      // Add other filters only if they have values
      Object.entries(filters).forEach(([key, value]) => {
        if (value && key !== "searchParams") {
          params[key] = value;
        }
      });

      const { data } = await axios.get(`${server}/scheme/all`, {
        params,
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });

      setSchemes(data?.data || []);
      setPagination((prev) => ({
        ...prev,
        totalCount: data?.count || 0,
        totalPages: Math.ceil((data?.count || 0) / pagination.pageSize),
      }));

      setSchemeStats({
        total: data?.count,
        totalDraftSchemes: data?.totalDraftSchemes,
        totalCompletedSchemes: data?.totalCompletedSchemes,
        totalAllocationAmount: data?.totalAllocationAmount,
      });

      updateFilterOptions(data?.data || []);
    } catch (error) {
      console.error("Error fetching schemes:", error);
      toast.error("Failed to fetch schemes");
    } finally {
      setLoading(false);
      setInitialLoading(false);
    }
  };

  const updateFilterOptions = (schemesData) => {
    const options = {
      districts: [
        ...new Set(schemesData.map((s) => s.district).filter(Boolean)),
      ],
      blocks: [...new Set(schemesData.map((s) => s.block).filter(Boolean))],
      gramPanchayats: [
        ...new Set(schemesData.map((s) => s.gramPanchayat).filter(Boolean)),
      ],
      sectors: [...new Set(schemesData.map((s) => s.sector).filter(Boolean))],
      subSectors: [
        ...new Set(schemesData.map((s) => s.subSector).filter(Boolean)),
      ],
      financialYears: [
        ...new Set(schemesData.map((s) => s.financialYear).filter(Boolean)),
      ],
      schemeStatuses: [
        ...new Set(schemesData.map((s) => s.schemeStatus).filter(Boolean)),
      ],
      tenderStatuses: [
        ...new Set(schemesData.map((s) => s.tenderStatus).filter(Boolean)),
      ],
      paymentStatuses: [
        ...new Set(schemesData.map((s) => s.paymentStatus).filter(Boolean)),
      ],
      ddcCumCEOs: [
        ...new Set(schemesData.map((s) => s.ddcCumCEOName).filter(Boolean)),
      ],
      districtEngineers: [
        ...new Set(
          schemesData.map((s) => s.districtEngineerName).filter(Boolean)
        ),
      ],
      ddos: [...new Set(schemesData.map((s) => s.ddo).filter(Boolean))],
    };

    setFilterOptions(options);
  };

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    // Reset to page 1 when filters change
    if (key !== "searchParams") {
      setPagination((prev) => ({ ...prev, pageNo: 1 }));
    }
  };

  const handleSearchChange = (value) => {
    setFilters((prev) => ({ ...prev, searchParams: value }));
    // Reset to page 1 when searching
    setPagination((prev) => ({ ...prev, pageNo: 1 }));
  };

  const removeFilter = (key) => {
    setFilters((prev) => ({ ...prev, [key]: "" }));
    if (key !== "searchParams") {
      setPagination((prev) => ({ ...prev, pageNo: 1 }));
    }
  };

  const handleSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };

  const clearFilters = () => {
    setFilters({
      searchParams: "",
      district: "",
      block: "",
      gramPanchayat: "",
      sector: "",
      subSector: "",
      financialYear: "",
      schemeStatus: "",
      tenderStatus: "",
      paymentStatus: "",
      ddcCumCEOName: "",
      districtEngineerName: "",
      ddo: "",
    });
    setPagination((prev) => ({ ...prev, pageNo: 1 }));
  };

  const handlePageChange = (event, value) => {
    setPagination((prev) => ({ ...prev, pageNo: value }));
  };

  const handlePageSizeChange = (event) => {
    setPagination((prev) => ({
      ...prev,
      pageSize: event.target.value,
      pageNo: 1, // Reset to first page when page size changes
    }));
  };

  const formatAmount = (amount) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 0,
    }).format(amount || 0);
  };

  const getActiveFilterCount = () => {
    return Object.values(filters).filter(
      (value) => value !== "" && value !== null && value !== undefined
    ).length;
  };

  const getFilterCategory = (key) => {
    const categoryMap = {
      district: "location",
      block: "location",
      gramPanchayat: "location",
      sector: "sector",
      subSector: "sector",
      financialYear: "financial",
      schemeStatus: "status",
      tenderStatus: "status",
      paymentStatus: "status",
      ddcCumCEOName: "officer",
      districtEngineerName: "officer",
      ddo: "officer",
      searchParams: "search",
    };
    return categoryMap[key] || "search";
  };

  const getFilterDisplayName = (key) => {
    const displayNames = {
      searchParams: "Search",
      district: "District",
      block: "Block",
      gramPanchayat: "Gram Panchayat",
      sector: "Sector",
      subSector: "Sub Sector",
      financialYear: "Financial Year",
      schemeStatus: "Scheme Status",
      tenderStatus: "Tender Status",
      paymentStatus: "Payment Status",
      ddcCumCEOName: "DDC-cum-CEO",
      districtEngineerName: "District Engineer",
      ddo: "DDO",
    };
    return displayNames[key] || key;
  };

  const getAppliedFilters = () => {
    return Object.entries(filters)
      .filter(([key, value]) => value && value !== "")
      .map(([key, value]) => ({
        key,
        value,
        category: getFilterCategory(key),
        displayName: getFilterDisplayName(key),
      }));
  };

  const handleRowClick = (schemeId) => {
    navigate(`/scheme/${schemeId}`);
  };

  const handleDelete = async (id) => {
    let userRes = window.confirm("Are you sure, you want to delete this proposal?");
    if (!userRes) return;

    try {
      setLoading(true);
      const { data } = await axios.delete(`${server}/scheme/deleteById`, {
        params: {
          schemeId: id,
        },
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });

      if (data.success) {
        toast.success("Proposal deleted successfully");
        fetchSchemes();
      }
    } catch (error) {
      console.error("Error deleting scheme:", error);
      toast.error(error?.response?.data?.message || "Failed to delete proposal");
    } finally {
      setLoading(false);
    }
  };

  const handleExportToExcel = async () => {
    try {
      setExporting(true);
      toast.info("Preparing Excel export, please wait...", { autoClose: 2000 });

      // Fetch all schemes with current filter parameters but huge pageSize to get all matching results
      const params = {
        pageNo: 1,
        pageSize: 100000,
        userWise: "true",
        searchParams: filters.searchParams,
        schemeType: "New",
      };

      // Add other active filters
      Object.entries(filters).forEach(([key, value]) => {
        if (value && key !== "searchParams") {
          params[key] = value;
        }
      });

      const { data } = await axios.get(`${server}/scheme/all`, {
        params,
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });

      const allSchemes = data?.data || [];
      if (allSchemes.length === 0) {
        toast.info("No data available to export");
        return;
      }

      // Generate CSV rows
      const headers = Object.keys(headersMap);
      const csvRows = [];

      // Add Headers Row
      csvRows.push(headers.map(h => headersMap[h]).map(escapeCSVValue).join(","));

      // Add Data Rows
      allSchemes.forEach(scheme => {
        const values = headers.map(header => {
          const val = getFieldValue(scheme, header);
          return escapeCSVValue(val);
        });
        csvRows.push(values.join(","));
      });

      // Include UTF-8 BOM (\uFEFF) so Excel handles Hindi/rupee and other characters correctly
      const csvContent = "\uFEFF" + csvRows.join("\n");
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.setAttribute("href", url);
      link.setAttribute("download", `Proposal_List_${new Date().toISOString().slice(0, 10)}.csv`);
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.success("Excel data list exported successfully");
    } catch (error) {
      console.error("Error exporting schemes:", error);
      toast.error("Failed to export schemes to Excel");
    } finally {
      setExporting(false);
    }
  };

  if (initialLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-2 text-sm text-gray-600">Loading schemes...</p>
        </div>
      </div>
    );
  }

  const appliedFilters = getAppliedFilters();

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 py-8 font-sans">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-blue-700 to-indigo-700 tracking-tight">
              Proposals List
            </h1>
            <p className="text-gray-500 mt-2 font-medium">
              Manage and monitor development schemes
            </p>
          </div>
          <motion.button
            whileHover={{ scale: 1.02, boxShadow: "0 10px 15px -3px rgba(16, 185, 129, 0.2)" }}
            whileTap={{ scale: 0.98 }}
            onClick={handleExportToExcel}
            disabled={exporting}
            className="inline-flex items-center px-5 py-2.5 bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 disabled:from-emerald-300 disabled:to-teal-400 text-white rounded-xl font-semibold shadow-sm transition-all text-sm gap-2 self-start sm:self-auto cursor-pointer border border-emerald-600/20"
          >
            {exporting ? (
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
            ) : (
              <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"></path>
              </svg>
            )}
            {exporting ? "Exporting..." : "Export Excel (All Fields)"}
          </motion.button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <motion.div whileHover={{ y: -4 }} className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 shadow-sm hover:shadow-xl hover:shadow-blue-500/10 border border-blue-100 transition-all duration-300 relative overflow-hidden group">
            <div className="absolute top-0 right-0 w-24 h-24 bg-gradient-to-br from-blue-100 to-transparent opacity-50 rounded-bl-full -z-10 group-hover:scale-110 transition-transform"></div>
            <div className="flex items-center gap-4">
              <div className="p-3 bg-gradient-to-br from-blue-50 to-blue-100 text-blue-600 rounded-xl shadow-inner border border-blue-200/50">
                <span className="text-xl">📊</span>
              </div>
              <div>
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Total Schemes</p>
                <p className="text-2xl font-bold text-slate-800 mt-1">{schemeStats.total || 0}</p>
              </div>
            </div>
          </motion.div>

          <motion.div whileHover={{ y: -4 }} className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 shadow-sm hover:shadow-xl hover:shadow-emerald-500/10 border border-emerald-100 transition-all duration-300 relative overflow-hidden group">
            <div className="absolute top-0 right-0 w-24 h-24 bg-gradient-to-br from-emerald-100 to-transparent opacity-50 rounded-bl-full -z-10 group-hover:scale-110 transition-transform"></div>
            <div className="flex items-center gap-4">
              <div className="p-3 bg-gradient-to-br from-emerald-50 to-emerald-100 text-emerald-600 rounded-xl shadow-inner border border-emerald-200/50">
                <span className="text-xl">✅</span>
              </div>
              <div>
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Completed</p>
                <p className="text-2xl font-bold text-emerald-600 mt-1">{schemeStats?.totalCompletedSchemes || 0}</p>
              </div>
            </div>
          </motion.div>

          <motion.div whileHover={{ y: -4 }} className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 shadow-sm hover:shadow-xl hover:shadow-amber-500/10 border border-amber-100 transition-all duration-300 relative overflow-hidden group">
            <div className="absolute top-0 right-0 w-24 h-24 bg-gradient-to-br from-amber-100 to-transparent opacity-50 rounded-bl-full -z-10 group-hover:scale-110 transition-transform"></div>
            <div className="flex items-center gap-4">
              <div className="p-3 bg-gradient-to-br from-amber-50 to-amber-100 text-amber-600 rounded-xl shadow-inner border border-amber-200/50">
                <span className="text-xl">⚡</span>
              </div>
              <div>
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Draft</p>
                <p className="text-2xl font-bold text-amber-600 mt-1">{schemeStats?.totalDraftSchemes || 0}</p>
              </div>
            </div>
          </motion.div>

          <motion.div whileHover={{ y: -4 }} className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 shadow-sm hover:shadow-xl hover:shadow-indigo-500/10 border border-indigo-100 transition-all duration-300 relative overflow-hidden group">
            <div className="absolute top-0 right-0 w-24 h-24 bg-gradient-to-br from-indigo-100 to-transparent opacity-50 rounded-bl-full -z-10 group-hover:scale-110 transition-transform"></div>
            <div className="flex items-center gap-4">
              <div className="p-3 bg-gradient-to-br from-indigo-50 to-indigo-100 text-indigo-600 rounded-xl shadow-inner border border-indigo-200/50">
                <span className="text-xl">💰</span>
              </div>
              <div>
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider truncate" title="Total Allocation">Total Allocation</p>
                <p className="text-xl font-bold text-slate-800 mt-1">₹{schemeStats?.totalAllocationAmount?.toLocaleString("en-In") || 0}</p>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Search and Filter Bar */}
        <div
          ref={filtersRef}
          className="bg-white/90 backdrop-blur-md rounded-2xl shadow-sm border border-slate-200 p-6 mb-6 relative z-10"
        >
          <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center">
            <div className="flex-1 w-full">
              <div className="relative group">
                <input
                  type="text"
                  placeholder="Search schemes by name, location, or sector..."
                  value={filters.searchParams}
                  onChange={(e) => handleSearchChange(e.target.value)}
                  className="w-full pl-12 pr-4 py-3 border border-slate-200 rounded-xl focus:ring-4 focus:ring-blue-500/20 focus:border-blue-500 bg-slate-50 hover:bg-white transition-all duration-300 text-slate-700 shadow-sm"
                />
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none group-focus-within:text-blue-500 transition-colors">
                  {loading ? (
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-500"></div>
                  ) : (
                    <svg className="w-5 h-5 text-slate-400 group-focus-within:text-blue-500 transition-colors" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>
                  )}
                </div>
              </div>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`flex items-center px-5 py-3 rounded-xl font-semibold shadow-sm transition-all duration-300 active:scale-95 border ${showFilters ? 'bg-blue-50 text-blue-700 border-blue-200' : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-50'}`}
              >
                <svg className="w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" /></svg>
                Filters
                {getActiveFilterCount() > 0 && (
                  <span className="ml-2 bg-blue-600 text-white rounded-full px-2 py-0.5 text-xs flex items-center justify-center font-bold">
                    {getActiveFilterCount()}
                  </span>
                )}
              </button>

              {getActiveFilterCount() > 0 && (
                <button
                  onClick={clearFilters}
                  className="flex items-center px-5 py-3 bg-rose-50 text-rose-600 border border-rose-200 rounded-xl hover:bg-rose-100 font-semibold shadow-sm transition-all duration-300 active:scale-95"
                >
                  <svg className="w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                  Clear All
                </button>
              )}
            </div>
          </div>

          {/* Applied Filters */}
          {appliedFilters.length > 0 && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              className="mt-4 flex flex-wrap gap-2"
            >
              {appliedFilters.map((filter) => (
                <motion.div
                  key={filter.key}
                  initial={{ scale: 0.8, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  className={`inline-flex items-center px-3 py-1 rounded-lg border text-sm font-medium ${filterCategoryColors[filter.category].bg
                    } ${filterCategoryColors[filter.category].border} ${filterCategoryColors[filter.category].text
                    }`}
                >
                  <span className="mr-1">{filter.displayName}:</span>
                  <span className="font-semibold">{filter.value}</span>
                  <IconButton
                    onClick={() => removeFilter(filter.key)}
                    size="small"
                    sx={{ marginLeft: "4px" }}
                  >
                    <Close className="text-rose-600" fontSize="small" />
                  </IconButton>
                </motion.div>
              ))}
            </motion.div>
          )}

          {/* Advanced Filters */}
          <AnimatePresence>
            {showFilters && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                className="mt-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4"
              >
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    District
                  </label>
                  <select
                    value={filters.district}
                    onChange={(e) =>
                      handleFilterChange("district", e.target.value)
                    }
                    disabled={user?.userType !== "admin"}
                    className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                  >
                    <option value="">All Districts</option>
                    {uniqueDistricts.map((district) => (
                      <option key={district} value={district}>
                        {district}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Block
                  </label>
                  <select
                    value={filters.block}
                    onChange={(e) =>
                      handleFilterChange("block", e.target.value)
                    }
                    className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                  >
                    <option value="">All Blocks</option>
                    {uniqueBlocks.map((block) => (
                      <option key={block} value={block}>
                        {block}
                      </option>
                    ))}
                  </select>
                </div>



                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Sector
                  </label>
                  <select
                    value={filters.sector}
                    onChange={(e) =>
                      handleFilterChange("sector", e.target.value)
                    }
                    className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500 focus:border-purple-500 bg-white"
                  >
                    <option value="">All Sectors</option>
                    {sectorOptions.map((sector) => (
                      <option key={sector} value={sector}>
                        {sector}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Sub Sector
                  </label>
                  <select
                    value={filters.subSector}
                    onChange={(e) =>
                      handleFilterChange("subSector", e.target.value)
                    }
                    className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500 focus:border-purple-500 bg-white"
                  >
                    <option value="">All Sub Sectors</option>
                    {subSectorOptions.map((subSector) => (
                      <option key={subSector} value={subSector}>
                        {subSector}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Financial Year
                  </label>
                  <select
                    value={filters.financialYear}
                    onChange={(e) =>
                      handleFilterChange("financialYear", e.target.value)
                    }
                    className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-orange-500 bg-white"
                  >
                    <option value="">All FY</option>
                    {financialYearOptions.map((year) => (
                      <option key={year} value={year}>
                        {year}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Scheme Status
                  </label>
                  <select
                    value={filters.schemeStatus}
                    onChange={(e) =>
                      handleFilterChange("schemeStatus", e.target.value)
                    }
                    className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-green-500 focus:border-green-500 bg-white"
                  >
                    <option value="">All Status</option>
                    {schemeStatusOptions.map((status) => (
                      <option key={status} value={status}>
                        {status}
                      </option>
                    ))}
                  </select>
                </div>


              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Results Count and Pagination Controls */}
        <div className="mb-4 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <p className="text-slate-600 font-medium text-sm">
              Showing{" "}
              <span className="font-bold text-blue-600">
                {schemes.length}
              </span>{" "}
              of{" "}
              <span className="font-bold text-blue-600">
                {pagination.totalCount}
              </span>{" "}
              schemes
              {appliedFilters.length > 0 && (
                <span className="text-slate-400">
                  {" "}
                  • {appliedFilters.length} filter(s) applied
                </span>
              )}
            </p>
            <p className="text-slate-400 font-semibold text-xs mt-1">
              Page {pagination.pageNo} of {pagination.totalPages}
            </p>
          </div>

          <div className="flex items-center space-x-4">
            {/* Page Size Selector */}
            <FormControl size="small" sx={{ minWidth: 100 }}>
              <InputLabel>Show</InputLabel>
              <Select
                value={pagination.pageSize}
                label="Show"
                onChange={handlePageSizeChange}
              >
                <MenuItem value={5}>5</MenuItem>
                <MenuItem value={10}>10</MenuItem>
                <MenuItem value={25}>25</MenuItem>
                <MenuItem value={50}>50</MenuItem>
                <MenuItem value={100}>100</MenuItem>
              </Select>
            </FormControl>

            {/* MUI Pagination */}
            {pagination.totalPages > 0 && (
              <Box>
                <Pagination
                  count={pagination.totalPages}
                  page={pagination.pageNo}
                  onChange={handlePageChange}
                  color="primary"
                  size="medium"
                  showFirstButton
                  showLastButton
                  siblingCount={1}
                  boundaryCount={1}
                />
              </Box>
            )}
          </div>
        </div>

        {/* Schemes Table */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden relative z-0">
          {schemes.length === 0 ? (
            <div className="text-center py-16 bg-slate-50/50">
              <div className="text-5xl mb-4 text-slate-300">🔍</div>
              <h3 className="text-xl font-bold text-slate-800 mb-2">
                No schemes found
              </h3>
              <p className="text-slate-500 text-sm">
                Try adjusting your search criteria or filters
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm text-left">
                <thead className="bg-slate-50/80 text-slate-600 border-b border-slate-200 uppercase text-xs tracking-wider">
                  <tr>
                    <th
                      className="px-5 py-4 font-semibold cursor-pointer hover:bg-slate-100 transition-colors"
                      onClick={() => handleSort("proposalName")}
                    >
                      Proposal
                    </th>
                    <th
                      className="px-5 py-4 font-semibold cursor-pointer hover:bg-slate-100 transition-colors"
                      onClick={() => handleSort("schemeName")}
                    >
                      Scheme
                    </th>
                    <th className="px-5 py-4 font-semibold">
                      Location
                    </th>
                    <th className="px-5 py-4 font-semibold">
                      Sector
                    </th>
                    <th
                      className="px-5 py-4 font-semibold cursor-pointer hover:bg-slate-100 transition-colors"
                      onClick={() => handleSort("allocatedAmount")}
                    >
                      <div className="flex items-center">
                        Amount
                        {sortConfig.key === "allocatedAmount" && (
                          <span className="ml-2 text-blue-600">
                            {sortConfig.direction === "asc" ? "↑" : "↓"}
                          </span>
                        )}
                      </div>
                    </th>
                    <th
                      className="px-5 py-4 font-semibold cursor-pointer hover:bg-slate-100 transition-colors"
                      onClick={() => handleSort("revenueGenerated")}
                    >
                      <div className="flex items-center">
                        Revenue Generated
                        {sortConfig.key === "revenueGenerated" && (
                          <span className="ml-2 text-blue-600">
                            {sortConfig.direction === "asc" ? "↑" : "↓"}
                          </span>
                        )}
                      </div>
                    </th>
                    <th className="px-5 py-4 font-semibold">
                      Action
                    </th>
                    {user?.userType === "admin" && (
                      <th className="px-5 py-4 font-semibold text-center">
                        Delete
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 bg-white">
                  {schemes.map((scheme, index) => (
                    <motion.tr
                      key={scheme._id || index}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.05 }}
                      className="hover:bg-slate-50/80 cursor-pointer transition-colors group"
                      onClick={() => {
                        scheme?.formStatus === "completed"
                          ? navigate("../schemeDetails", {
                            state: { schemeId: scheme?.id },
                          })
                          : navigate((scheme?.schemeType === "existing" || scheme?.schemeType === "Legacy") ? "../existingSchemeEntry" : "../addNewScheme", {
                            state: {
                              draftSchemeId: scheme?.id,
                              draftSchemeData: scheme,
                            },
                          });
                      }}
                    >
                      <td className="px-5 py-4">
                        <span className="text-slate-700 font-medium">{scheme.proposalName || "---"}</span>
                      </td>
                      <td className="px-5 py-4">
                        <div>
                          <div className="font-semibold text-slate-900 line-clamp-1">
                            {scheme.schemeName}
                          </div>
                          <div className="text-slate-500 text-xs mt-1 font-medium bg-slate-100 inline-block px-2 py-0.5 rounded">
                            FY: {scheme.financialYear}
                          </div>
                        </div>
                      </td>
                      <td className="px-5 py-4">
                        <div className="font-medium text-slate-800">
                          {scheme.district}
                        </div>
                        <div className="text-slate-500 text-xs mt-0.5">
                          {scheme.block} • {scheme.gramPanchayat}
                        </div>
                      </td>
                      <td className="px-5 py-4">
                        <div className="font-medium text-slate-800">
                          {scheme.sector}
                        </div>
                        <div className="text-slate-500 text-xs mt-0.5">
                          {scheme.subSector}
                        </div>
                      </td>
                      <td className="px-5 py-4">
                        <div className="font-bold text-emerald-600 bg-emerald-50 inline-block px-2.5 py-1 rounded-lg border border-emerald-100">
                          {formatAmount(scheme.allocatedAmount)}
                        </div>
                      </td>
                      <td className="px-5 py-4">
                        <div className="font-bold text-teal-700 bg-teal-50 inline-block px-2.5 py-1 rounded-lg border border-teal-100">
                          {formatAmount(scheme.revenueGenerated ?? scheme.totalRevenueGenerated ?? 0)}
                        </div>
                      </td>
                      <td
                        className="px-5 py-4"
                        onMouseDown={(e) => e.stopPropagation()}
                      >
                        <div className="flex items-center gap-3">
                          {scheme?.formStatus === "completed" && (
                            <button
                              className="font-bold text-blue-600 hover:text-blue-800"
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate("../schemeDetails", {
                                  state: { schemeId: scheme?.id },
                                });
                              }}
                            >
                              View
                            </button>
                          )}
                          {scheme?.formStatus === "draft" && (
                            <button
                              className="font-bold text-blue-600 hover:text-blue-800"
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate((scheme?.schemeType === "existing" || scheme?.schemeType === "Legacy") ? "../existingSchemeEntry" : "../addNewScheme", {
                                  state: {
                                    draftSchemeId: scheme?.id,
                                    draftSchemeData: scheme,
                                  },
                                });
                              }}
                            >
                              Edit
                            </button>
                          )}
                        </div>
                      </td>
                      {user?.userType === "admin" && (
                        <td
                          className="px-5 py-4 text-center"
                          onMouseDown={(e) => e.stopPropagation()}
                        >
                          <button
                            className="p-2 text-rose-500 hover:text-rose-700 hover:bg-rose-50 rounded-lg transition-colors"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDelete(scheme?.id);
                            }}
                            title="Delete Proposal"
                          >
                            <FaTrash />
                          </button>
                        </td>
                      )}
                    </motion.tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Bottom Pagination */}
        {schemes.length > 0 && (
          <div className="mt-6 flex justify-center">
            <Box>
              <Pagination
                count={pagination.totalPages}
                page={pagination.pageNo}
                onChange={handlePageChange}
                color="primary"
                size="large"
                showFirstButton
                showLastButton
                siblingCount={1}
                boundaryCount={1}
              />
            </Box>
          </div>
        )}
      </div>
    </div>
  );
};

export default SchemesList;
